<script setup>
import { ref, onMounted } from 'vue';

import AnButton from "@/components/common/AnButton.vue";
import ProgressBar from "@/sections/my-goals/ProgressBar.vue";
import { convertToInr } from "@/services/common-services";
import { useUserStore } from "@/store/user-store";
import { computed } from "vue";
import AnLoader from "@/components/common/AnLoader.vue";
import { Knob } from "primevue";

const store = useUserStore();
const screeners = computed(() => store.screenerData || null);

const isExpanded = ref({});

const truncatedText = (text) => {
  if (!text) return '';
  return text.split(' ').slice(0, 15).join(' ') + '...';
};

const toggleExpand = (name) => {
  isExpanded.value[name] = !isExpanded.value[name];
};

const shouldShowToggle = (text) => {
  if (!text) return false;
  return text.split(' ').length > 15;
};

const props = defineProps({
  setSection: Function,
  setSelectedScreener: Function
});

const viewStocks = (screener) => {
  props.setSelectedScreener(screener);
  props.setSection(2);
};

</script>

<template>
  <div class="section-container">
    <div class="screener-grid">
      <div v-for="screener in screeners" :key="screener.Query_Name" class="card">
        <i class="pi pi-filter card-icon"></i>
        <h3 class="card-title">{{ screener.screenerName }}</h3>
        <p class="card-description">
          <span v-if="!isExpanded[screener.screenerName] && shouldShowToggle(screener.screenerDesc)">
            {{ truncatedText(screener.screenerDesc) }}
          </span>
          <span v-else>
            {{ screener.screenerDesc }}
          </span>
        </p>

        <AnButton v-if="shouldShowToggle(screener.screenerDesc)"
                @click="toggleExpand(screener.screenerName)"
                class="p-button-text read-toggle"
                :label="isExpanded[screener.screenerName] ? 'Read Less' : 'Read More'" />
        <AnButton label="View Stocks" class="p-button-primary mt-10" @click="viewStocks(screener)" />
      </div>
    </div>
  </div>
</template>

<style scoped>

/* Screener Grid */
.screener-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 25px;
  margin-top: -60px;
  align-items: stretch;
}

/* Card Styling */
.card {
  background-color: var(--an-white);
  border-radius: 10px;
  box-shadow: var(--an-card-shadow);
  padding: 25px;
  display: flex;
  flex-direction: column;
  min-height: 350px;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.card:hover {
  transform: translateY(-5px);
  box-shadow: 0px 12px 36px rgba(0, 0, 0, 0.1);
}

.card-icon {
  font-size: 28px;
  color: var(--an-primary);
  margin-bottom: 15px;
  align-self: flex-start;
}

.card-title {
  min-height: 60px;
  display: flex;
  align-items: center;
  margin-bottom: 15px;
  font-size: 1.3rem;
  font-weight: 600;
}

.card-description {
  flex: 1;
  margin-bottom: 20px;
  line-height: 1.6;
  font-size: 1rem;
  color: var(--an-text-secondary);
  overflow-wrap: break-word;
}

.read-toggle {
  margin-bottom: 15px !important;
  font-size: 0.95rem !important;
}

/* Stock Table */
.stock-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 20px;
}

.stock-table th, .stock-table td {
  padding: 12px 15px;
  text-align: left;
  font-size: 14px;
}

.stock-table th {
  background-color: var(--an-background-light);
  font-weight: 600;
}

.stock-table tr:nth-child(even) {
  background-color: var(--an-white2);
}

.stock-table tr:hover {
  background-color: var(--an-background-light);
  cursor: pointer;
}

.pagination-controls {
  display: flex;
  justify-content: space-between;
  margin-top: 20px;
}
</style>
